import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { MapPin, Calendar, Euro, CheckCircle, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlacementDirectPage() {
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    food_truck_id: "", date_debut: "", date_fin: "", lieu: "", titre: "", montant: 0, nom_client: "", email_client: "", type_repas: "", info_complementaire: "", heure_evenement: "", heure_arrivee: "", contact_sur_place: "",
  });

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const loadFoodTrucks = async () => {
    try {
      const r = await axios.get(`${API}/api/food-trucks`, authHeader());
      setFoodTrucks((r.data || []).filter((f) => (f.statut || "actif") === "actif"));
    } catch (e) { /* ignore */ }
  };
  const loadPlacements = async () => {
    try {
      const r = await axios.get(`${API}/api/placement-direct`, authHeader());
      setPlacements(r.data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadFoodTrucks(); loadPlacements(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!form.food_truck_id || !form.date_debut) {
      setMessage({ type: "error", text: "Choisissez un food truck et une date." });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, montant: parseFloat(form.montant) || 0 };
      const r = await axios.post(`${API}/api/placement-direct`, payload, authHeader());
      setMessage({ type: "success", text: `Placement cree pour ${r.data.food_truck}. ${r.data.email_envoye ? "Food truck notifie par email." : ""}` });
      setForm({ food_truck_id: "", date_debut: "", date_fin: "", lieu: "", titre: "", montant: 0, nom_client: "", email_client: "", type_repas: "", info_complementaire: "", heure_evenement: "", heure_arrivee: "", contact_sur_place: "" });
      loadPlacements();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur lors du placement." });
    } finally { setSaving(false); }
  };

  // Duplication : recopie les reglages d'un placement dans le formulaire de creation.
  // Le food truck et la date restent modifiables (cas d'usage : placer un AUTRE food
  // truck sur le meme evenement, ou le meme food truck sur une nouvelle date).
  const dupliquer = (p) => {
    setForm({
      food_truck_id: p.food_truck_id || "",
      date_debut: p.date_debut || "",
      date_fin: p.date_fin || "",
      lieu: p.lieu || "",
      titre: p.titre || "",
      montant: p.montant_du || 0,
      nom_client: p.nom_client || "",
      email_client: p.email_client || "",
      type_repas: p.type_repas || "",
      info_complementaire: p.info_complementaire || "",
      heure_evenement: p.heure_evenement || "",
      heure_arrivee: p.heure_arrivee || "",
      contact_sur_place: p.contact_sur_place || "",
    });
    setMessage({ type: "success", text: "Reglages copies. Choisissez le food truck et/ou la date, puis validez." });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-secondary">Placement Direct</h1>
        <p className="text-sm text-gray-500">Placez un food truck sur une date, sans appel a participation. Facture, sondage et bilan s'enchainent automatiquement.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Nouveau placement</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Food truck *</Label>
              <select
                className="w-full mt-1 border rounded-md h-10 px-3 text-sm"
                value={form.food_truck_id}
                onChange={(e) => setForm({ ...form, food_truck_id: e.target.value })}
              >
                <option value="">-- Choisir un food truck --</option>
                {foodTrucks.map((f) => (
                  <option key={f.id} value={f.id}>{f.nom_food_truck}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Date de debut *</Label>
              <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
            </div>
            <div>
              <Label>Date de fin</Label>
              <Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} />
            </div>
            <div>
              <Label>Lieu</Label>
              <Input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Ville / adresse" />
            </div>
            <div>
              <Label>Intitule (optionnel)</Label>
              <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Nom de la prestation" />
            </div>
            <div>
              <Label>Montant participation (EUR)</Label>
              <Input type="number" step="0.01" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} />
            </div>
            <div>
                <Label>Nom du client</Label>
                <Input value={form.nom_client} onChange={(e) => setForm({ ...form, nom_client: e.target.value })} placeholder="Nom du client" />
            </div>
            <div>
                <Label>Email client (pour le sondage)</Label>
                <Input type="email" value={form.email_client} onChange={(e) => setForm({ ...form, email_client: e.target.value })} placeholder="client@email.fr" />
            </div>
            <div>
                <Label>Type de repas</Label>
                <select value={form.type_repas} onChange={(e) => setForm({ ...form, type_repas: e.target.value })} className="w-full border rounded-md h-10 px-3">
                  <option value="">-- Choisir --</option>
                  <option value="midi">Midi</option>
                  <option value="soir">Soir</option>
                  <option value="journee_continue">Journee continue (2 services)</option>
                </select>
            </div>
            <div>
                <Label>Heure evenement</Label>
                <Input value={form.heure_evenement} onChange={(e) => setForm({ ...form, heure_evenement: e.target.value })} placeholder="ex: 11h00" />
            </div>
            <div>
                <Label>Heure arrivee food truck</Label>
                <Input value={form.heure_arrivee} onChange={(e) => setForm({ ...form, heure_arrivee: e.target.value })} placeholder="ex: 09h30" />
            </div>
            <div>
                <Label>Contact sur place</Label>
                <Input value={form.contact_sur_place} onChange={(e) => setForm({ ...form, contact_sur_place: e.target.value })} placeholder="Nom + telephone" />
            </div>
            <div className="sm:col-span-2">
                <Label>Information complementaire pour le food truck</Label>
                <Input value={form.info_complementaire} onChange={(e) => setForm({ ...form, info_complementaire: e.target.value })} placeholder="Infos logistiques, acces, electricite..." />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Button type="submit" disabled={saving}>{saving ? "Placement..." : "Placer le food truck"}</Button>
              {message && (
                <span className={message.type === "success" ? "text-green-600 text-sm" : "text-red-600 text-sm"}>{message.text}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Placements ({placements.length})</CardTitle></CardHeader>
        <CardContent>
          {placements.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun placement direct pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {placements.map((p, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-3">
                  <div className="flex flex-col">
                    <span className="font-semibold text-secondary">{p.food_truck_nom}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />{p.date_debut}
                      {p.lieu ? (<><MapPin className="w-3 h-3 ml-2" />{p.lieu}</>) : null}
                    </span>
                    <span className="text-xs text-gray-400">{p.titre}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="gap-1"><Euro className="w-3 h-3" />{p.montant_du}</Badge>
                    <Badge variant={p.statut_paiement === "paye" ? "default" : "outline"}>{p.statut_paiement}</Badge>
                    {p.bilan_rempli ? (<Badge className="gap-1 bg-green-600"><CheckCircle className="w-3 h-3" />bilan</Badge>) : null}
                    <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => dupliquer(p)}>
                      <Copy className="w-3 h-3" />Dupliquer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
